import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { V2File } from '../v2Upload.d';
import { CamExtractedData, CamToolpath } from '@/workers/camTypes';
import FileNameInput from './FileNameInput';
import { FIELD_LABEL } from './fileCardStyles';

const round = (n: number) => Math.round(n * 100) / 100;

type Field = { label: string; value: string };

/** A cell, dropped when the value is absent. */
const field = (
  label: string,
  value: string | number | null | undefined
): Field | null =>
  value === null || value === undefined || value === ''
    ? null
    : { label, value: String(value) };

/** Short unit for the numeric cells; null when the file does not say, so no
 * unit is shown rather than a guessed one. */
function unitOf(x: CamExtractedData): string | null {
  if (x.units === 'MM') return 'mm';
  return x.units === 'Inches' ? 'in' : null;
}

const withUnit = (value: string, u: string | null) =>
  u ? `${value} ${u}` : value;

/** Everything the parser read out of the design file. Absent fields are
 * dropped, so a format that does not record one simply does not show it —
 * `.art` records only a stock thickness, for instance. */
export function fields(file: V2File, x: CamExtractedData): Field[] {
  const u = unitOf(x);
  const m = x.material;
  return [
    field('Design SW', x.software),
    field('Format', `.${file.ext}`),
    field('File size', file.size),
    field('Version', x.version),
    field('Units', x.units),
    field(
      'Stock',
      m &&
        withUnit(
          [m.width, m.height, m.thickness]
            .filter((v): v is number => v !== null)
            .map(round)
            .join(' × '),
          u
        )
    ),
    field('Toolpaths', x.toolpaths.length || null),
  ].filter((f): f is Field => Boolean(f));
}

/** The tool a toolpath runs, as one muted line: what it is, then its geometry.
 * Empty when the file named neither — a `.crv` written before the tool was
 * assigned, for instance. */
function toolGeometry(tp: CamToolpath): string {
  return [
    tp.toolName,
    tp.bitType,
    tp.toolDia != null ? `⌀ ${round(tp.toolDia)} in` : null,
    tp.vAngle != null ? `${tp.vAngle}°` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** Speed and feed, each shown only when the file recorded it. */
function ToolpathBadges({
  tp,
  unit,
}: {
  tp: CamToolpath;
  unit: string | null;
}) {
  return (
    <div className="shrink-0 flex items-center gap-1.5">
      {tp.spindle != null && (
        <Badge variant="secondary" className="text-[10px] font-normal">
          {tp.spindle.toLocaleString()} rpm
        </Badge>
      )}
      {tp.feed != null && (
        <Badge variant="secondary" className="text-[10px] font-normal">
          {withUnit(tp.feed.toLocaleString(), unit && `${unit}/min`)}
        </Badge>
      )}
    </div>
  );
}

/** One toolpath as a row: what it is called, the tool it uses, and the speeds
 * it runs at pushed to the right. */
function ToolpathRow({
  tp,
  index,
  unit,
}: {
  tp: CamToolpath;
  index: number;
  unit: string | null;
}) {
  const geometry = toolGeometry(tp);

  return (
    <div className="flex items-center gap-3 px-3 py-2.5">
      <span className="w-8 h-8 shrink-0 rounded-full bg-muted text-heading flex items-center justify-center text-[11px] font-semibold">
        {index + 1}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[13px] font-semibold truncate">
          {tp.opName ?? tp.bitType ?? 'Toolpath'}
        </div>
        {geometry && (
          <div className="text-[11px] font-tertiary text-muted-foreground truncate mt-0.5">
            {geometry}
          </div>
        )}
      </div>
      <ToolpathBadges tp={tp} unit={unit} />
    </div>
  );
}

/** Why there is nothing to show. A failed parse is said plainly rather than
 * left as "still reading", which would never resolve. */
function EmptyState({ parsing }: { parsing: boolean }) {
  return (
    <p className="text-xs text-muted-foreground">
      {parsing
        ? 'Still reading this file…'
        : "We couldn't read this design file. It still uploads and downloads with your project — fill the details in yourself."}
    </p>
  );
}

/**
 * The full view a Carveco/Vectric design file opens to — the same shape as
 * GcodeDetailModal, minus the 3D toolpath: these formats store their geometry
 * as the design, not as machine moves, so there is nothing to render.
 *
 * `.crv` carries a stock size but no tool library (Vectric keeps tools in an
 * external database), while `.art` carries tools and speeds but only a stock
 * thickness.
 * The grid drops whatever the format does not record rather than showing gaps.
 */
export default function CamDetailModal({
  file,
  extracted,
  parsing,
  onClose,
}: {
  file: V2File | null;
  extracted: CamExtractedData | null;
  /** A read is actually in flight. Without this the empty state claims to be
   * still reading a file whose parse already failed. */
  parsing: boolean;
  onClose: () => void;
}) {
  return (
    <Dialog open={Boolean(file)} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-2xl gap-0 p-0 overflow-hidden">
        {file && (
          <ScrollArea className="max-h-[92vh]">
            <DialogTitle className="sr-only">{file.name}</DialogTitle>
            <DialogDescription className="sr-only">
              The details read out of this design file.
            </DialogDescription>
            <div className="grid gap-4 p-6">
              <div>
                <div className={cn(FIELD_LABEL, 'mb-1.5')}>Filename</div>
                <FileNameInput file={file} />
              </div>

              {extracted ? (
                <>
                  <div>
                    <div className={cn(FIELD_LABEL, 'mb-2')}>
                      Read from this file
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-border border rounded-xl overflow-hidden">
                      {fields(file, extracted).map((f) => (
                        <div key={f.label} className="bg-card p-3">
                          <div className={FIELD_LABEL}>{f.label}</div>
                          <div
                            title={f.value}
                            className="text-sm font-medium mt-1 truncate"
                          >
                            {f.value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {extracted.toolpaths.length > 0 && (
                    <div>
                      <div className={cn(FIELD_LABEL, 'mb-2')}>
                        Toolpaths ({extracted.toolpaths.length})
                      </div>
                      <div className="rounded-xl border divide-y overflow-hidden">
                        {extracted.toolpaths.map((tp, i) => (
                          <ToolpathRow
                            // Names repeat across passes and the list never
                            // reorders, so the index disambiguates.
                            // eslint-disable-next-line react/no-array-index-key
                            key={`${tp.opName ?? 'tp'}-${i}`}
                            tp={tp}
                            index={i}
                            unit={unitOf(extracted)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <EmptyState parsing={parsing} />
              )}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}
